import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as _moment from 'moment';

const moment = _moment;

export interface FiltroConfig {
  key: string;
  label: string;
  chipLabel: string;
  type: 'date' | 'multi-select' | 'single-select' | 'date-range';
  options?: any[];
  optionLabel?: string;
  multiple?: boolean;
  showButtons?: boolean;
  appearance?: string;
  minDate?: Date;
  maxDate?: Date;
}

@Component({
  selector: 'app-filtros-reportes',
  templateUrl: './filtros-reportes.component.html',
  styleUrls: ['./filtros-reportes.component.scss']
})
export class FiltrosReportesComponent implements OnInit {
  @Input() config: FiltroConfig[] = [];
  @Input() initialValues: any = {};
  @Output() filterChange = new EventEmitter<any>();
  @Output() removeFilter = new EventEmitter<string>();

  filtrosForm: FormGroup;
  activeChips: { key: string; value: string; chipLabel: string }[] = [];

  constructor(private fb: FormBuilder) { }

  pendingSelections: { [key: string]: any } = {};

ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const formGroup = {};
    this.config.forEach(filtro => {
      formGroup[filtro.key] = [this.initialValues[filtro.key] || null];
      this.pendingSelections[filtro.key] = this.initialValues[filtro.key] || null;

      if (this.initialValues[filtro.key]) {
        this.updateChip(filtro.key, this.initialValues[filtro.key]);
      }
    });

    this.filtrosForm = this.fb.group(formGroup);
  }

  private updateFormWithInitialValues(): void {
    this.config.forEach(filtro => {
      const control = this.filtrosForm.get(filtro.key);
      const newValue = this.initialValues[filtro.key];
      
      if (control && newValue !== undefined) {
        control.setValue(newValue);
        this.pendingSelections[filtro.key] = newValue;
        this.updateChip(filtro.key, newValue);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialValues && this.filtrosForm) {
      this.updateFormWithInitialValues();
    }
  }

  updateChip(key: string, value: any): void {
    const existingChipIndex = this.activeChips.findIndex(chip => chip.key === key);

    if (value && (Array.isArray(value) ? value.length > 0 : value instanceof Date || value)) {
      const chipValue = this.getChipDisplayValue(key, value);

      if (existingChipIndex > -1) {
        this.activeChips[existingChipIndex].value = chipValue;
      } else {
        this.activeChips.push({ 
          key, 
          value: chipValue, 
          chipLabel: this.config.find(c => c.key === key)?.chipLabel || key 
        });
      }
    } else if (existingChipIndex > -1) {
      this.activeChips.splice(existingChipIndex, 1);
    }
  }

  getChipDisplayValue(key: string, value: any): string {
    const config = this.config.find(c => c.key === key);

    if (!config) return '';

    switch (config.type) {
      case 'date':
        return moment(value).format('DD/MM/YYYY');

      case 'date-range':
        const from = moment(value.date_from).format('DD/MM/YYYY');
        const to = moment(value.date_to).format('DD/MM/YYYY');
        return `Desde: ${from} Hasta: ${to}`;

      case 'multi-select':
        if (Array.isArray(value) && value.length > 1) return `Varios(${value.length})`;
        if (Array.isArray(value) && value.length === 1) {
          return config.optionLabel ? value[0][config.optionLabel] : value[0];
        }
        return config.optionLabel ? value[config.optionLabel] : value;

      case 'single-select':
        return config.optionLabel ? value[config.optionLabel] : value;

      default:
        return value?.toString() || '';
    }
  }

  onDateChange(key: string, event: any): void {
    this.filtrosForm.get(key).setValue(event.value);
    this.updateChip(key, event.value);
    // Para fechas, emitir inmediatamente
    this.filterChange.emit(this.filtrosForm.value);
  }

  onSelectionChange(key: string, value: any): void {
    const config = this.config.find(c => c.key === key);
    
    // Si no tiene botones o no es múltiple, aplicar inmediatamente
    if (!config?.showButtons || !config?.multiple) {
      this.filtrosForm.get(key).setValue(value);
      this.updateChip(key, value);
      this.filterChange.emit(this.filtrosForm.value);
    } else {
      // Si tiene botones, solo guardar como pendiente
      this.pendingSelections[key] = value;
    }
  }

  removeChip(key: string): void {
    this.filtrosForm.get(key).reset();
    this.pendingSelections[key] = null;
    this.updateChip(key, null);
    this.removeFilter.emit(key);
  }

  compareFn(o1: any, o2: any): boolean {
    return o1 && o2 ? JSON.stringify(o1) === JSON.stringify(o2) : false;
  }

  confirmSelection(key: string): void {
    // Actualizar el valor del formulario con la selección pendiente
    this.filtrosForm.get(key).setValue(this.pendingSelections[key]);
    this.updateChip(key, this.pendingSelections[key]);
    this.filterChange.emit(this.filtrosForm.value);
  }

  cancelSelection(key: string): void {
    // Restaurar la selección pendiente al valor actual del formulario
    this.pendingSelections[key] = this.filtrosForm.get(key).value;
  }
}